import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { type AuthResponse, exchangeImpersonationCode } from "@/lib/data";
import { signOut, startSession } from "@/lib/session";

type ImpersonateSearch = { code?: string };

export const Route = createFileRoute("/impersonate")({
  component: ImpersonatePage,
  validateSearch: (search: Record<string, unknown>): ImpersonateSearch => ({
    code: typeof search.code === "string" ? search.code : undefined,
  }),
});

// Impersonation codes are single-use; if the exchange fires twice for the
// same code (e.g. StrictMode's double-invoked effect, a route remount, or
// any other re-trigger), the second request's 401 races with the first
// request's success and can stomp it — leaving the code consumed but the
// user signed out. Caching the in-flight Promise per code dedupes both the
// network request and the response handling, so every subscriber observes
// the same result. Module-level state survives remounts where a useRef
// would not.
const exchangeRequests = new Map<string, Promise<AuthResponse>>();

function exchangeOnce(code: string): Promise<AuthResponse> {
  const existing = exchangeRequests.get(code);
  if (existing) return existing;

  const promise = exchangeImpersonationCode(code);
  exchangeRequests.set(code, promise);
  return promise;
}

export function __resetImpersonationCacheForTests() {
  exchangeRequests.clear();
}

function ImpersonatePage() {
  const { code } = Route.useSearch();
  const navigate = useNavigate();
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const error = code ? exchangeError : "Missing impersonation code.";

  useEffect(() => {
    if (!code) return;

    let cancelled = false;

    exchangeOnce(code)
      .then(async (response) => {
        if (cancelled) return;
        const target = await startSession(response.token);
        if (cancelled) return;
        void navigate({ ...target, replace: true });
      })
      .catch(() => {
        if (cancelled) return;
        setExchangeError("This impersonation link is invalid or has expired.");
      });

    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  return (
    <div className="relative min-h-screen bg-background">
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center px-5">
        {error ? (
          <div className="w-full rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-elevated)]">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertCircle className="size-6" />
            </div>
            <h1 className="mt-4 font-heading text-xl font-semibold text-foreground">
              Unable to impersonate
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {error}
            </p>
            {/* A failed impersonation must not leave the rep inside whichever
                Applicant was signed in before; signing out lets the root send
                them to the sign-in form. */}
            <Button className="mt-5 w-full" size="lg" onClick={signOut}>
              Go to sign in
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            <span className="text-sm">Signing you in…</span>
          </div>
        )}
      </div>
    </div>
  );
}
